import React, { useState } from 'react';
import { OpenAIClient } from '../../utils/openaiClient';

const EvoMLSDLC = () => {
  const [selectedPhase, setSelectedPhase] = useState('discovery');
  const [selectedView, setSelectedView] = useState('technical'); // 'technical' or 'stories'
  const [showDetails, setShowDetails] = useState({});
  const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);
  const [summaryProgress, setSummaryProgress] = useState('');

  const toggleDetails = (id) => {
    setShowDetails(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  // Function to generate markdown summary
  const generateMarkdownSummary = async () => {
    setIsGeneratingSummary(true);
    setSummaryProgress('Initializing AI client...');
    
    try {
      const openai = new OpenAIClient();
      
      setSummaryProgress('Generating comprehensive AutoML guide...');
      
      const summaryPrompt = `Generate a comprehensive markdown guide for EvoML AutoML Platform. Include:

1. Executive Summary of EvoML as an AutoML platform
2. Detailed breakdown of each SDLC phase:
   - Data Discovery & Preparation
   - Model Development & Optimization
   - Code Generation & Export
   - Local Deployment & Integration
3. Implementation guides for each phase
4. Code examples showing generated Python code
5. ROI and benefits analysis for AutoML adoption
6. Mermaid diagrams showing the AutoML workflow
7. Comparison with traditional ML development

Make it detailed, technical, and suitable for data scientists and ML engineers.`;

      const summary = await openai.getChatCompletion(summaryPrompt, {
        systemPrompt: `You are a technical documentation expert specializing in AutoML platforms and MLOps. Create detailed markdown documentation with Mermaid diagrams, Python code examples, and implementation guides. Focus on practical, actionable content for data scientists and ML engineers.`,
        temperature: 0.7,
        maxTokens: 4000
      });

      // Create the full markdown document
      const fullMarkdown = `# EvoML AutoML Platform - SDLC Integration Guide

Generated on: ${new Date().toLocaleString()}

---

${summary}

---

## Appendix: Quick Start Code

### Python Client Installation
\`\`\`bash
pip install evoml-client

# Or for local development
pip install -e git+https://github.com/turintech/evoml-client.git
\`\`\`

### Basic AutoML Pipeline
\`\`\`python
from evoml import EvoMLClient

# Initialize client
client = EvoMLClient(api_key="your-key")

# Upload data
dataset = client.upload_data("sales_data.csv")

# Run AutoML
pipeline = client.automl(
    dataset_id=dataset.id,
    target_column="revenue",
    optimization_metric="rmse",
    time_budget_minutes=30
)

# Get generated code
code = pipeline.get_python_code()

# Deploy locally
pipeline.deploy_local(port=8080)
\`\`\`

---

*This document was generated using EvoML documentation tools.*`;

      // Download the markdown file
      setSummaryProgress('Preparing download...');
      const blob = new Blob([fullMarkdown], { type: 'text/markdown' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `evoml-automl-guide-${Date.now()}.md`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      setSummaryProgress('Guide downloaded successfully!');
      setTimeout(() => {
        setIsGeneratingSummary(false);
        setSummaryProgress('');
      }, 2000);
      
    } catch (error) {
      console.error('Error generating summary:', error);
      setSummaryProgress('Error: ' + error.message);
      setTimeout(() => {
        setIsGeneratingSummary(false);
        setSummaryProgress('');
      }, 3000);
    }
  };

  const generateExecutiveSummary = async () => {
    const summary = `# EvoML: Enterprise AutoML Platform

## Executive Summary

EvoML revolutionizes machine learning development by automatically generating production-ready Python code for your ML pipelines. Unlike black-box AutoML solutions, EvoML gives you complete ownership of the generated code, enabling local deployment and customization.

## Key Differentiators

### 🎯 Code Generation, Not Black Box
- **Traditional AutoML**: API-only access, vendor lock-in
- **EvoML**: Complete Python code you own and can modify
- **Result**: No vendor lock-in, full transparency

### 🚀 Local Deployment Ready
- Generate standalone Python packages
- Deploy on your infrastructure
- No cloud dependencies required
- Docker and Kubernetes ready

### 🧬 Multi-Objective Optimization
- Optimize for accuracy AND inference speed
- Balance model size with performance
- Consider deployment constraints
- Hardware-aware optimization

## Use Cases for Enterprise

### 1. 📊 Rapid Prototyping
- **Challenge**: Need ML model in days, not months
- **Solution**: Upload data, get optimized code in hours
- **Result**: 10x faster time-to-market

### 2. 🏢 On-Premise Deployment
- **Challenge**: Data cannot leave company servers
- **Solution**: Generated code runs entirely locally
- **Result**: Complete data privacy compliance

### 3. 💰 Cost Optimization
- **Challenge**: Cloud ML services too expensive at scale
- **Solution**: One-time generation, unlimited local inference
- **Result**: 90% cost reduction vs cloud APIs

### 4. 🔧 Custom Integration
- **Challenge**: Need to integrate ML with existing systems
- **Solution**: Modify generated code to fit architecture
- **Result**: Seamless integration with legacy systems

## The AutoML Process

### Week 1: Data Upload & Exploration
\`\`\`
1. Upload your dataset (CSV, Parquet, Database)
2. EvoML analyzes data quality & patterns
3. Get insights and recommendations
\`\`\`

### Week 2: Automated Model Development
\`\`\`
1. Select target variable and constraints
2. EvoML explores 1000s of pipelines
3. Multi-objective optimization runs
\`\`\`

### Week 3: Code Generation & Testing
\`\`\`
1. Download optimized Python code
2. Review generated documentation
3. Run local tests and validation
\`\`\`

### Week 4: Production Deployment
\`\`\`
1. Deploy using Docker/Kubernetes
2. Monitor performance metrics
3. Iterate with EvoML if needed
\`\`\`

## Generated Code Example

\`\`\`python
# Generated by EvoML - Fully Documented & Production Ready
class OptimizedPipeline:
    """EvoML-generated pipeline for sales prediction
    Accuracy: 94.2% | Inference: 2.3ms | Size: 4.2MB
    """
    
    def __init__(self):
        self.preprocessor = self._build_preprocessor()
        self.model = self._build_model()
        
    def predict(self, X):
        """Make predictions with optimized pipeline"""
        X_processed = self.preprocessor.transform(X)
        return self.model.predict(X_processed)
        
    def deploy_api(self, port=8080):
        """Deploy as REST API locally"""
        app = self._create_flask_app()
        app.run(host='0.0.0.0', port=port)
\`\`\`

## Why EvoML for Enterprises?

### ✅ Complete Ownership
- Generated code is yours forever
- No recurring API costs
- Modify and extend as needed

### ✅ Compliance Ready
- Run entirely on-premise
- No data leaves your servers
- Full audit trail of model decisions

### ✅ Integration Friendly
- Standard Python code
- Works with existing CI/CD
- Compatible with any deployment platform

## ROI Metrics

- **80% faster** model development
- **90% lower** deployment costs
- **100% code** ownership
- **0% vendor** lock-in

## Next Steps

1. **Free Trial**: Test with your data
2. **Pilot Project**: 30-day engagement
3. **Enterprise License**: Unlimited usage

## Contact

**Sales**: sales@turintech.ai
**Technical**: support@turintech.ai
**Website**: https://evoml.turintech.ai

---

*EvoML: AutoML that gives you the code*`;

    const blob = new Blob([summary], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'evoml-executive-summary.md';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Visual components
  const SDLCPhase = ({ phase, icon, title, description, isActive, onClick }) => (
    <div
      onClick={onClick}
      style={{
        background: isActive 
          ? 'linear-gradient(135deg, rgba(156, 39, 176, 0.2), rgba(156, 39, 176, 0.1))' 
          : 'rgba(255, 255, 255, 0.05)',
        padding: '20px',
        borderRadius: '12px',
        border: `2px solid ${isActive ? '#9c27b0' : 'rgba(255, 255, 255, 0.1)'}`,
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        transform: isActive ? 'scale(1.02)' : 'scale(1)',
        textAlign: 'center'
      }}
    >
      <div style={{ fontSize: '36px', marginBottom: '10px' }}>{icon}</div>
      <h4 style={{ 
        margin: '0 0 5px 0', 
        fontSize: '16px',
        color: isActive ? '#9c27b0' : 'white'
      }}>
        {title}
      </h4>
      <p style={{ 
        margin: 0, 
        fontSize: '12px', 
        opacity: 0.8,
        lineHeight: '1.4'
      }}>
        {description}
      </p>
    </div>
  );

  const FeatureCard = ({ icon, title, description, features, color = '#9c27b0' }) => (
    <div style={{
      background: 'rgba(0, 0, 0, 0.3)',
      borderRadius: '12px',
      padding: '25px',
      border: `1px solid ${color}40`,
      height: '100%'
    }}>
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        marginBottom: '15px',
        gap: '15px'
      }}>
        <div style={{
          fontSize: '32px',
          width: '50px',
          height: '50px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: `${color}20`,
          borderRadius: '10px'
        }}>
          {icon}
        </div>
        <h3 style={{ margin: 0, fontSize: '20px', color }}>{title}</h3>
      </div>
      <p style={{ 
        margin: '0 0 20px 0', 
        fontSize: '14px', 
        opacity: 0.9,
        lineHeight: '1.5'
      }}>
        {description}
      </p>
      <ul style={{ 
        margin: 0, 
        paddingLeft: '20px',
        fontSize: '13px',
        lineHeight: '1.8'
      }}>
        {features.map((feature, i) => (
          <li key={i} style={{ marginBottom: '8px' }}>{feature}</li>
        ))}
      </ul>
    </div>
  );

  const IntegrationFlow = ({ title, steps, color }) => (
    <div style={{
      background: 'rgba(0, 0, 0, 0.2)',
      borderRadius: '12px',
      padding: '20px',
      marginBottom: '20px'
    }}>
      <h4 style={{ margin: '0 0 20px 0', color, fontSize: '18px' }}>{title}</h4>
      <div style={{ position: 'relative' }}>
        {steps.map((step, i) => (
          <div key={i} style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: i < steps.length - 1 ? '20px' : '0'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: color,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: '600',
              fontSize: '16px',
              flexShrink: 0
            }}>
              {i + 1}
            </div>
            <div style={{
              marginLeft: '20px',
              flex: 1,
              background: 'rgba(255, 255, 255, 0.05)',
              padding: '12px 16px',
              borderRadius: '8px',
              fontSize: '14px'
            }}>
              <strong>{step.action}:</strong> {step.description}
            </div>
            {i < steps.length - 1 && (
              <div style={{
                position: 'absolute',
                left: '20px',
                top: `${40 + i * 60}px`,
                width: '2px',
                height: '20px',
                background: `${color}40`
              }} />
            )}
          </div>
        ))}
      </div>
    </div>
  );

  const CodeExample = ({ title, code, language = 'python', color = '#9c27b0' }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    
    return (
      <div style={{
        background: 'rgba(0, 0, 0, 0.4)',
        borderRadius: '8px',
        overflow: 'hidden',
        border: `1px solid ${color}40`,
        marginBottom: '20px'
      }}>
        <div 
          onClick={() => setIsExpanded(!isExpanded)}
          style={{
            background: `${color}20`,
            padding: '10px 15px',
            borderBottom: `1px solid ${color}40`,
            fontSize: '13px',
            fontWeight: '600',
            color: color,
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            userSelect: 'none'
          }}
        >
          <span>{title}</span>
          <span style={{
            fontSize: '16px',
            transition: 'transform 0.3s ease',
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
          }}>
            ▼
          </span>
        </div>
        {isExpanded && (
          <pre style={{
            margin: 0,
            padding: '15px',
            fontSize: '12px',
            lineHeight: '1.5',
            color: '#ddd',
            overflow: 'auto',
            maxHeight: '400px',
            animation: 'slideDown 0.3s ease'
          }}>
            <code>{code}</code>
          </pre>
        )}
      </div>
    );
  };

  const MetricCard = ({ label, value, trend, color }) => (
    <div style={{
      background: 'rgba(0, 0, 0, 0.3)',
      padding: '20px',
      borderRadius: '8px',
      textAlign: 'center',
      border: `1px solid ${color}40`
    }}>
      <div style={{ fontSize: '32px', fontWeight: '700', color, marginBottom: '5px' }}>
        {value}
      </div>
      <div style={{ fontSize: '14px', opacity: 0.8, marginBottom: '5px' }}>
        {label}
      </div>
      {trend && (
        <div style={{ fontSize: '12px', color: trend > 0 ? '#4ECDC4' : '#FF6B6B' }}>
          {trend > 0 ? '↑' : '↓'} {Math.abs(trend)}%
        </div>
      )}
    </div>
  );

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
      color: 'white',
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
      padding: '40px 20px'
    }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        <h1 style={{ 
          margin: '0 0 15px 0', 
          fontSize: '42px',
          fontWeight: '700',
          background: 'linear-gradient(135deg, #9c27b0, #e91e63)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent'
        }}>
          🧬 EvoML in the SDLC Process
        </h1>
        <p style={{ margin: '0 0 40px 0', fontSize: '20px', opacity: 0.8 }}>
          AutoML platform that generates production-ready Python code for local deployment
        </p>

        {/* Summary Generation Buttons */}
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '15px',
          marginBottom: '20px'
        }}>
          <button
            onClick={generateExecutiveSummary}
            style={{
              padding: '12px 24px',
              background: 'linear-gradient(135deg, #e91e63, #f06292)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}
          >
            💼 Executive Summary
          </button>
          <button
            onClick={generateMarkdownSummary}
            disabled={isGeneratingSummary}
            style={{
              padding: '12px 24px',
              background: isGeneratingSummary ? '#666' : 'linear-gradient(135deg, #9c27b0, #ab47bc)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: isGeneratingSummary ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}
          >
            {isGeneratingSummary ? (
              <>
                <span style={{
                  display: 'inline-block',
                  width: '16px',
                  height: '16px',
                  border: '2px solid white',
                  borderTopColor: 'transparent',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
                {summaryProgress || 'Generating...'}
              </>
            ) : (
              <>
                📄 Generate AutoML Guide
              </>
            )}
          </button>
        </div>

        <style>
          {`
            @keyframes spin {
              to { transform: rotate(360deg); }
            }
            @keyframes slideDown {
              from {
                opacity: 0;
                transform: translateY(-10px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }
            @keyframes pulse {
              0%, 100% { opacity: 1; }
              50% { opacity: 0.5; }
            }
          `}
        </style>

        {/* View Toggle */}
        <div style={{
          display: 'flex',
          gap: '10px',
          marginBottom: '30px',
          justifyContent: 'center'
        }}>
          <button
            onClick={() => setSelectedView('technical')}
            style={{
              padding: '12px 30px',
              background: selectedView === 'technical' 
                ? 'linear-gradient(135deg, #9c27b0, #ab47bc)' 
                : 'rgba(255, 255, 255, 0.1)',
              color: 'white',
              border: `2px solid ${selectedView === 'technical' ? '#9c27b0' : 'rgba(255, 255, 255, 0.2)'}`,
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
          >
            🔧 Technical View
          </button>
          <button
            onClick={() => setSelectedView('stories')}
            style={{
              padding: '12px 30px',
              background: selectedView === 'stories' 
                ? 'linear-gradient(135deg, #e91e63, #f06292)' 
                : 'rgba(255, 255, 255, 0.1)',
              color: 'white',
              border: `2px solid ${selectedView === 'stories' ? '#e91e63' : 'rgba(255, 255, 255, 0.2)'}`,
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
          >
            👥 User Stories
          </button>
        </div>

        {/* Technical View */}
        {selectedView === 'technical' && (
          <>
            {/* SDLC Phases */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              padding: '30px',
              borderRadius: '16px',
              marginBottom: '40px'
            }}>
              <h2 style={{ margin: '0 0 25px 0', fontSize: '24px' }}>
                Select SDLC Phase
              </h2>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '20px'
              }}>
                <SDLCPhase
                  phase="discovery"
                  icon="🔍"
                  title="Data Discovery"
                  description="Upload data, explore patterns, get insights"
                  isActive={selectedPhase === 'discovery'}
                  onClick={() => setSelectedPhase('discovery')}
                />
                <SDLCPhase
                  phase="development"
                  icon="🧬"
                  title="Model Development"
                  description="Automated ML pipeline optimization"
                  isActive={selectedPhase === 'development'}
                  onClick={() => setSelectedPhase('development')}
                />
                <SDLCPhase
                  phase="generation"
                  icon="💻"
                  title="Code Generation"
                  description="Export production-ready Python code"
                  isActive={selectedPhase === 'generation'}
                  onClick={() => setSelectedPhase('generation')}
                />
                <SDLCPhase
                  phase="deployment"
                  icon="🚀"
                  title="Local Deployment"
                  description="Deploy on-premise or cloud"
                  isActive={selectedPhase === 'deployment'}
                  onClick={() => setSelectedPhase('deployment')}
                />
              </div>
            </div>

            {/* Data Discovery Phase */}
            {selectedPhase === 'discovery' && (
              <div>
                {/* Data Upload Interface */}
                <div style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  padding: '40px',
                  borderRadius: '16px',
                  marginBottom: '30px'
                }}>
                  <h2 style={{ margin: '0 0 30px 0', color: '#9c27b0' }}>
                    🔍 Data Discovery & Analysis
                  </h2>
                  
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                    gap: '25px',
                    marginBottom: '30px'
                  }}>
                    <FeatureCard
                      icon="📤"
                      title="Multi-Source Upload"
                      description="Connect to any data source seamlessly"
                      features={[
                        'CSV, Excel, Parquet files',
                        'Direct database connections',
                        'Cloud storage (S3, GCS, Azure)',
                        'Real-time streaming data',
                        'API endpoint ingestion'
                      ]}
                      color="#9c27b0"
                    />
                    
                    <FeatureCard
                      icon="🔬"
                      title="Automated EDA"
                      description="Comprehensive data analysis in seconds"
                      features={[
                        'Statistical summaries',
                        'Distribution analysis',
                        'Correlation matrices',
                        'Missing value patterns',
                        'Outlier detection'
                      ]}
                      color="#e91e63"
                    />
                    
                    <FeatureCard
                      icon="💡"
                      title="ML Readiness"
                      description="Assess your data's ML potential"
                      features={[
                        'Feature importance preview',
                        'Target leakage detection',
                        'Class imbalance analysis',
                        'Data quality scoring',
                        'Recommended ML tasks'
                      ]}
                      color="#2196f3"
                    />
                  </div>

                  <IntegrationFlow
                    title="Data Discovery Workflow"
                    color="#9c27b0"
                    steps={[
                      {
                        action: "Upload Data",
                        description: "Drag & drop or connect to data source"
                      },
                      {
                        action: "Automatic Profiling",
                        description: "EvoML analyzes structure, quality, patterns"
                      },
                      {
                        action: "Interactive Exploration",
                        description: "Visual dashboards with insights"
                      },
                      {
                        action: "ML Recommendations",
                        description: "Suggested targets and model types"
                      },
                      {
                        action: "Configure Pipeline",
                        description: "Set objectives and constraints"
                      }
                    ]}
                  />

                  <CodeExample
                    title="Python Client - Data Upload"
                    color="#9c27b0"
                    code={`from evoml import EvoMLClient
import pandas as pd

# Initialize EvoML client
client = EvoMLClient(
    api_key="your-api-key",
    base_url="https://api.evoml.com"  # Or your local instance
)

# Upload from file
dataset = client.upload_data(
    file_path="sales_data.csv",
    name="Q4 Sales Analysis",
    description="Historical sales data with customer features"
)

# Or upload from DataFrame
df = pd.read_sql("SELECT * FROM sales", connection)
dataset = client.upload_dataframe(
    dataframe=df,
    name="Live Sales Data"
)

# Or connect to database directly
dataset = client.connect_database(
    connection_string="postgresql://user:pass@host/db",
    query="SELECT * FROM sales WHERE year >= 2020",
    name="Recent Sales Data"
)

# Get automated insights
insights = dataset.get_insights()
print(f"Data Quality Score: {insights.quality_score}")
print(f"Recommended ML Tasks: {insights.ml_tasks}")
print(f"Top Features: {insights.important_features[:5]}")`}
                  />

                  {/* Data Insights Dashboard */}
                  <div style={{
                    background: 'rgba(156, 39, 176, 0.1)',
                    padding: '30px',
                    borderRadius: '12px',
                    marginBottom: '30px'
                  }}>
                    <h3 style={{ margin: '0 0 20px 0', color: '#9c27b0' }}>
                      📊 Sample Data Insights Dashboard
                    </h3>
                    
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                      gap: '15px',
                      marginBottom: '25px'
                    }}>
                      <MetricCard label="Rows" value="1.2M" color="#9c27b0" />
                      <MetricCard label="Features" value="47" color="#e91e63" />
                      <MetricCard label="Quality Score" value="8.9/10" trend={5} color="#2196f3" />
                      <MetricCard label="ML Readiness" value="High" color="#4caf50" />
                    </div>

                    <div style={{
                      background: 'rgba(0, 0, 0, 0.3)',
                      padding: '20px',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}>
                      <strong>🎯 Recommended ML Tasks:</strong>
                      <ul style={{ margin: '10px 0 0 20px' }}>
                        <li>Regression: Predict revenue (high correlation features detected)</li>
                        <li>Classification: Customer churn prediction (clear patterns found)</li>
                        <li>Time Series: Sales forecasting (temporal patterns identified)</li>
                        <li>Clustering: Customer segmentation (natural groups detected)</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Model Development Phase */}
            {selectedPhase === 'development' && (
              <div>
                <div style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  padding: '40px',
                  borderRadius: '16px',
                  marginBottom: '30px'
                }}>
                  <h2 style={{ margin: '0 0 30px 0', color: '#e91e63' }}>
                    🧬 Automated Model Development
                  </h2>
                  
                  {/* AutoML Process */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                    gap: '25px',
                    marginBottom: '30px'
                  }}>
                    <FeatureCard
                      icon="🎯"
                      title="Multi-Objective Optimization"
                      description="Balance multiple goals simultaneously"
                      features={[
                        'Accuracy vs inference speed',
                        'Model size vs performance',
                        'Training time vs quality',
                        'Memory usage constraints',
                        'Hardware-specific optimization'
                      ]}
                      color="#e91e63"
                    />
                    
                    <FeatureCard
                      icon="🧪"
                      title="Pipeline Evolution"
                      description="Genetic algorithms explore possibilities"
                      features={[
                        '1000s of pipeline variations',
                        'Feature engineering automation',
                        'Model ensemble creation',
                        'Hyperparameter optimization',
                        'Architecture search'
                      ]}
                      color="#9c27b0"
                    />
                    
                    <FeatureCard
                      icon="🏆"
                      title="Best Model Selection"
                      description="Pareto-optimal solutions for your needs"
                      features={[
                        'Trade-off visualization',
                        'Business metric alignment',
                        'Deployment constraint checking',
                        'Explainability scoring',
                        'Robustness testing'
                      ]}
                      color="#2196f3"
                    />
                  </div>

                  {/* Live Training Dashboard */}
                  <div style={{
                    background: 'rgba(233, 30, 99, 0.1)',
                    padding: '30px',
                    borderRadius: '12px',
                    marginBottom: '30px'
                  }}>
                    <h3 style={{ margin: '0 0 20px 0', color: '#e91e63' }}>
                      ⚡ Live Training Progress
                    </h3>
                    
                    <div style={{
                      background: 'rgba(0, 0, 0, 0.3)',
                      padding: '20px',
                      borderRadius: '8px',
                      marginBottom: '20px'
                    }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '10px'
                      }}>
                        <span style={{ fontSize: '16px', fontWeight: '600' }}>
                          Generation 42 of 100
                        </span>
                        <span style={{ fontSize: '14px', opacity: 0.8 }}>
                          Time remaining: ~12 minutes
                        </span>
                      </div>
                      <div style={{
                        height: '20px',
                        background: 'rgba(255, 255, 255, 0.1)',
                        borderRadius: '10px',
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          height: '100%',
                          width: '42%',
                          background: 'linear-gradient(90deg, #e91e63, #f06292)',
                          transition: 'width 0.5s ease'
                        }} />
                      </div>
                    </div>

                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(3, 1fr)',
                      gap: '15px',
                      marginBottom: '20px'
                    }}>
                      <div style={{
                        background: 'rgba(0, 0, 0, 0.2)',
                        padding: '15px',
                        borderRadius: '8px',
                        textAlign: 'center'
                      }}>
                        <div style={{ fontSize: '24px', fontWeight: '700', color: '#e91e63' }}>
                          847
                        </div>
                        <div style={{ fontSize: '12px', opacity: 0.8 }}>
                          Pipelines Evaluated
                        </div>
                      </div>
                      <div style={{
                        background: 'rgba(0, 0, 0, 0.2)',
                        padding: '15px',
                        borderRadius: '8px',
                        textAlign: 'center'
                      }}>
                        <div style={{ fontSize: '24px', fontWeight: '700', color: '#9c27b0' }}>
                          94.2%
                        </div>
                        <div style={{ fontSize: '12px', opacity: 0.8 }}>
                          Best Accuracy
                        </div>
                      </div>
                      <div style={{
                        background: 'rgba(0, 0, 0, 0.2)',
                        padding: '15px',
                        borderRadius: '8px',
                        textAlign: 'center'
                      }}>
                        <div style={{ fontSize: '24px', fontWeight: '700', color: '#2196f3' }}>
                          2.3ms
                        </div>
                        <div style={{ fontSize: '12px', opacity: 0.8 }}>
                          Inference Time
                        </div>
                      </div>
                    </div>

                    <div style={{
                      background: 'rgba(0, 0, 0, 0.2)',
                      padding: '15px',
                      borderRadius: '8px',
                      fontSize: '13px'
                    }}>
                      <strong>🏆 Current Best Pipeline:</strong>
                      <div style={{ marginTop: '10px', fontFamily: 'monospace' }}>
                        StandardScaler → PolynomialFeatures → XGBoost(n_estimators=127, max_depth=8)
                      </div>
                    </div>
                  </div>

                  <CodeExample
                    title="AutoML Configuration & Execution"
                    color="#e91e63"
                    code={`# Configure AutoML run with constraints
automl_config = {
    "target_column": "revenue",
    "task_type": "regression",
    "optimization_objectives": [
        {"metric": "rmse", "weight": 0.7},
        {"metric": "inference_time", "weight": 0.3}
    ],
    "constraints": {
        "max_model_size_mb": 50,
        "max_inference_time_ms": 5,
        "min_accuracy": 0.90
    },
    "time_budget_minutes": 30,
    "hardware_target": "cpu"  # or "gpu", "edge"
}

# Start AutoML
job = client.start_automl(
    dataset_id=dataset.id,
    config=automl_config
)

# Monitor progress
while not job.is_complete():
    status = job.get_status()
    print(f"Progress: {status.progress}%")
    print(f"Best Score: {status.best_score}")
    print(f"Pipelines Evaluated: {status.pipelines_evaluated}")
    time.sleep(10)

# Get results
results = job.get_results()
print(f"Best Pipeline: {results.best_pipeline}")
print(f"Pareto Front: {len(results.pareto_solutions)} solutions")

# Visualize trade-offs
results.plot_pareto_front(
    x_axis="rmse",
    y_axis="inference_time",
    save_path="pareto_front.png"
)`}
                  />
                </div>
              </div>
            )}

            {/* Code Generation Phase */}
            {selectedPhase === 'generation' && (
              <div>
                <div style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  padding: '40px',
                  borderRadius: '16px',
                  marginBottom: '30px'
                }}>
                  <h2 style={{ margin: '0 0 30px 0', color: '#2196f3' }}>
                    💻 Production Code Generation
                  </h2>
                  
                  {/* Code Generation Options */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                    gap: '25px',
                    marginBottom: '30px'
                  }}>
                    <FeatureCard
                      icon="📦"
                      title="Complete Package"
                      description="Everything needed for production"
                      features={[
                        'Standalone Python module',
                        'All dependencies included',
                        'Comprehensive documentation',
                        'Unit & integration tests',
                        'Performance benchmarks'
                      ]}
                      color="#2196f3"
                    />
                    
                    <FeatureCard
                      icon="🔧"
                      title="Customizable Code"
                      description="Modify and extend as needed"
                      features={[
                        'Clean, readable Python',
                        'Modular architecture',
                        'Configuration options',
                        'Custom preprocessing hooks',
                        'Extensible predictions'
                      ]}
                      color="#9c27b0"
                    />
                    
                    <FeatureCard
                      icon="🚀"
                      title="Deployment Ready"
                      description="Multiple deployment options"
                      features={[
                        'REST API server included',
                        'Batch processing support',
                        'Docker containerization',
                        'Kubernetes manifests',
                        'Cloud function exports'
                      ]}
                      color="#4caf50"
                    />
                  </div>

                  {/* Generated Code Preview */}
                  <div style={{
                    background: 'rgba(33, 150, 243, 0.1)',
                    padding: '30px',
                    borderRadius: '12px',
                    marginBottom: '30px'
                  }}>
                    <h3 style={{ margin: '0 0 20px 0', color: '#2196f3' }}>
                      📄 Generated Code Structure
                    </h3>
                    
                    <div style={{
                      background: 'rgba(0, 0, 0, 0.3)',
                      padding: '20px',
                      borderRadius: '8px',
                      fontFamily: 'monospace',
                      fontSize: '13px',
                      lineHeight: '1.6'
                    }}>
                      <div style={{ color: '#4caf50' }}>evoml_generated_pipeline/</div>
                      <div style={{ marginLeft: '20px' }}>
                        <div style={{ color: '#2196f3' }}>├── pipeline/</div>
                        <div style={{ marginLeft: '20px' }}>
                          <div>│   ├── __init__.py</div>
                          <div>│   ├── model.py <span style={{ color: '#666' }}># Core model class</span></div>
                          <div>│   ├── preprocessor.py <span style={{ color: '#666' }}># Data preprocessing</span></div>
                          <div>│   ├── features.py <span style={{ color: '#666' }}># Feature engineering</span></div>
                          <div>│   └── config.py <span style={{ color: '#666' }}># Configuration</span></div>
                        </div>
                        <div style={{ color: '#2196f3' }}>├── api/</div>
                        <div style={{ marginLeft: '20px' }}>
                          <div>│   ├── server.py <span style={{ color: '#666' }}># FastAPI server</span></div>
                          <div>│   └── schemas.py <span style={{ color: '#666' }}># Request/response models</span></div>
                        </div>
                        <div style={{ color: '#2196f3' }}>├── tests/</div>
                        <div style={{ marginLeft: '20px' }}>
                          <div>│   ├── test_model.py</div>
                          <div>│   ├── test_api.py</div>
                          <div>│   └── test_performance.py</div>
                        </div>
                        <div style={{ color: '#2196f3' }}>├── deployment/</div>
                        <div style={{ marginLeft: '20px' }}>
                          <div>│   ├── Dockerfile</div>
                          <div>│   ├── docker-compose.yml</div>
                          <div>│   └── kubernetes.yaml</div>
                        </div>
                        <div>├── requirements.txt</div>
                        <div>├── setup.py</div>
                        <div>├── README.md</div>
                        <div>└── benchmark_results.json</div>
                      </div>
                    </div>
                  </div>

                  <CodeExample
                    title="Generated Model Code Example"
                    color="#2196f3"
                    code={`# Generated by EvoML - Optimized for your data and constraints
# Model: XGBoost with custom preprocessing
# Performance: 94.2% accuracy, 2.3ms inference time

import numpy as np
import pandas as pd
from typing import Union, List
import joblib
import warnings
warnings.filterwarnings('ignore')

class EvoMLPipeline:
    """Production-ready ML pipeline generated by EvoML
    
    This pipeline was optimized for:
    - Primary metric: RMSE (0.0234)
    - Secondary metric: Inference time (2.3ms)
    - Hardware: CPU deployment
    - Model size: 4.2MB
    """
    
    def __init__(self, model_path: str = "models/"):
        self.model_path = model_path
        self._load_components()
        
    def _load_components(self):
        """Load all pipeline components"""
        self.preprocessor = joblib.load(f"{self.model_path}/preprocessor.pkl")
        self.feature_engineer = joblib.load(f"{self.model_path}/features.pkl")
        self.model = joblib.load(f"{self.model_path}/model.pkl")
        self.feature_names = joblib.load(f"{self.model_path}/features.pkl")
        
    def predict(self, 
                data: Union[pd.DataFrame, np.ndarray, dict],
                return_confidence: bool = False) -> np.ndarray:
        """Make predictions on new data
        
        Args:
            data: Input data (DataFrame, array, or dict)
            return_confidence: Return confidence intervals
            
        Returns:
            predictions: Array of predictions
        """
        # Convert input to DataFrame
        if isinstance(data, dict):
            data = pd.DataFrame([data])
        elif isinstance(data, np.ndarray):
            data = pd.DataFrame(data, columns=self.feature_names)
            
        # Preprocessing pipeline
        X = self.preprocessor.transform(data)
        
        # Feature engineering
        X = self.feature_engineer.transform(X)
        
        # Make predictions
        predictions = self.model.predict(X)
        
        if return_confidence:
            # Calculate prediction intervals
            lower, upper = self._calculate_intervals(X)
            return predictions, lower, upper
            
        return predictions
    
    def predict_proba(self, data: Union[pd.DataFrame, np.ndarray]) -> np.ndarray:
        """Get probability predictions (classification only)"""
        X = self._preprocess(data)
        return self.model.predict_proba(X)
    
    def explain_prediction(self, data: pd.DataFrame, index: int = 0):
        """Generate explanation for a single prediction"""
        # SHAP values calculation
        import shap
        explainer = shap.TreeExplainer(self.model)
        X = self._preprocess(data)
        shap_values = explainer.shap_values(X)
        
        return {
            "prediction": self.predict(data)[index],
            "feature_importance": dict(zip(self.feature_names, shap_values[index])),
            "base_value": explainer.expected_value
        }
    
    def run_diagnostics(self) -> dict:
        """Run model diagnostics and health checks"""
        return {
            "model_type": type(self.model).__name__,
            "n_features": len(self.feature_names),
            "model_size_mb": self._get_model_size(),
            "average_inference_time_ms": self._benchmark_inference(),
            "memory_usage_mb": self._get_memory_usage()
        }

# API Server Integration
from fastapi import FastAPI, HTTPException
from pydantic import BaseModel

app = FastAPI(title="EvoML Generated Model API")
pipeline = EvoMLPipeline()

class PredictionRequest(BaseModel):
    data: dict
    return_confidence: bool = False

@app.post("/predict")
async def predict(request: PredictionRequest):
    try:
        result = pipeline.predict(request.data, request.return_confidence)
        return {"prediction": result.tolist()}
    except Exception as e:
        raise HTTPException(status_code=400, detail=str(e))

@app.get("/health")
async def health():
    diagnostics = pipeline.run_diagnostics()
    return {"status": "healthy", "diagnostics": diagnostics}

if __name__ == "__main__":
    import uvicorn
    uvicorn.run(app, host="0.0.0.0", port=8080)`}
                  />

                  <CodeExample
                    title="Download and Export Options"
                    color="#4caf50"
                    code={`# Download complete package
package = job.download_package(
    output_path="./my_model",
    include_options={
        "api_server": True,
        "docker_files": True,
        "kubernetes_manifests": True,
        "tests": True,
        "documentation": True
    }
)

# Or get specific components
model_code = job.get_model_code()
preprocessing_code = job.get_preprocessing_code()
api_code = job.get_api_code()

# Export to different formats
job.export_to_onnx("model.onnx")  # For edge deployment
job.export_to_tensorflow("model_tf/")  # TensorFlow format
job.export_to_pmml("model.pmml")  # PMML standard

# Generate language bindings
job.generate_bindings("java", output_path="java_client/")
job.generate_bindings("cpp", output_path="cpp_client/")

# Create deployment packages
job.create_docker_image(
    image_name="my-model:latest",
    base_image="python:3.9-slim"
)

job.create_kubernetes_deployment(
    name="revenue-predictor",
    replicas=3,
    resources={"cpu": "1", "memory": "2Gi"}
)`}
                  />
                </div>
              </div>
            )}

            {/* Deployment Phase */}
            {selectedPhase === 'deployment' && (
              <div>
                <div style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  padding: '40px',
                  borderRadius: '16px',
                  marginBottom: '30px'
                }}>
                  <h2 style={{ margin: '0 0 30px 0', color: '#4caf50' }}>
                    🚀 Local & Cloud Deployment
                  </h2>
                  
                  {/* Deployment Options */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                    gap: '25px',
                    marginBottom: '30px'
                  }}>
                    <FeatureCard
                      icon="🏠"
                      title="Local Deployment"
                      description="Run on your infrastructure"
                      features={[
                        'Single server deployment',
                        'Docker containers',
                        'Kubernetes clusters',
                        'Edge devices',
                        'Air-gapped environments'
                      ]}
                      color="#4caf50"
                    />
                    
                    <FeatureCard
                      icon="☁️"
                      title="Cloud Deployment"
                      description="Scale with cloud providers"
                      features={[
                        'AWS SageMaker export',
                        'Google Vertex AI',
                        'Azure ML integration',
                        'Serverless functions',
                        'Auto-scaling groups'
                      ]}
                      color="#2196f3"
                    />
                    
                    <FeatureCard
                      icon="🔌"
                      title="Integration Options"
                      description="Connect to existing systems"
                      features={[
                        'REST API endpoints',
                        'gRPC services',
                        'Message queue integration',
                        'Batch processing',
                        'Stream processing'
                      ]}
                      color="#9c27b0"
                    />
                    
                    <FeatureCard
                      icon="📊"
                      title="Monitoring"
                      description="Track model performance"
                      features={[
                        'Prometheus metrics',
                        'Custom dashboards',
                        'Drift detection',
                        'A/B testing support',
                        'Performance alerts'
                      ]}
                      color="#ff9800"
                    />
                  </div>

                  {/* Deployment Commands */}
                  <div style={{
                    background: 'rgba(76, 175, 80, 0.1)',
                    padding: '30px',
                    borderRadius: '12px',
                    marginBottom: '30px'
                  }}>
                    <h3 style={{ margin: '0 0 20px 0', color: '#4caf50' }}>
                      🚀 Quick Deployment Options
                    </h3>
                    
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                      gap: '20px'
                    }}>
                      <div style={{
                        background: 'rgba(0, 0, 0, 0.3)',
                        padding: '20px',
                        borderRadius: '8px'
                      }}>
                        <h4 style={{ margin: '0 0 10px 0', fontSize: '16px', color: '#4caf50' }}>
                          🐳 Docker Deployment
                        </h4>
                        <pre style={{
                          margin: 0,
                          fontSize: '12px',
                          lineHeight: '1.5',
                          opacity: 0.9
                        }}>
{`docker build -t my-model .
docker run -p 8080:8080 my-model`}
                        </pre>
                      </div>
                      
                      <div style={{
                        background: 'rgba(0, 0, 0, 0.3)',
                        padding: '20px',
                        borderRadius: '8px'
                      }}>
                        <h4 style={{ margin: '0 0 10px 0', fontSize: '16px', color: '#2196f3' }}>
                          ☸️ Kubernetes Deployment
                        </h4>
                        <pre style={{
                          margin: 0,
                          fontSize: '12px',
                          lineHeight: '1.5',
                          opacity: 0.9
                        }}>
{`kubectl apply -f deployment.yaml
kubectl expose deployment my-model`}
                        </pre>
                      </div>
                      
                      <div style={{
                        background: 'rgba(0, 0, 0, 0.3)',
                        padding: '20px',
                        borderRadius: '8px'
                      }}>
                        <h4 style={{ margin: '0 0 10px 0', fontSize: '16px', color: '#9c27b0' }}>
                          🐍 Python Direct
                        </h4>
                        <pre style={{
                          margin: 0,
                          fontSize: '12px',
                          lineHeight: '1.5',
                          opacity: 0.9
                        }}>
{`pip install -r requirements.txt
python api/server.py`}
                        </pre>
                      </div>
                    </div>
                  </div>

                  <CodeExample
                    title="Production Deployment Script"
                    color="#4caf50"
                    code={`#!/bin/bash
# EvoML Generated Deployment Script

echo "🚀 Deploying EvoML Model..."

# 1. Environment Setup
echo "Setting up environment..."
python -m venv venv
source venv/bin/activate
pip install -r requirements.txt

# 2. Run Tests
echo "Running tests..."
pytest tests/ -v --cov=pipeline

# 3. Performance Benchmark
echo "Running performance benchmark..."
python -m pipeline.benchmark --iterations 1000

# 4. Build Docker Image
echo "Building Docker image..."
docker build -t evoml-model:latest .

# 5. Run Health Checks
echo "Running health checks..."
docker run --rm evoml-model:latest python -m pipeline.health_check

# 6. Deploy to Kubernetes
echo "Deploying to Kubernetes..."
kubectl apply -f deployment/kubernetes.yaml

# 7. Wait for Ready
kubectl wait --for=condition=ready pod -l app=evoml-model --timeout=300s

# 8. Run Smoke Tests
echo "Running smoke tests..."
kubectl exec -it deploy/evoml-model -- python -m tests.smoke_test

echo "✅ Deployment complete!"
echo "API available at: http://localhost:8080"
echo "Metrics available at: http://localhost:8080/metrics"
echo "Health check: http://localhost:8080/health"`}
                  />

                  <CodeExample
                    title="Monitoring and Maintenance"
                    color="#ff9800"
                    code={`from evoml.monitoring import ModelMonitor
import prometheus_client

# Initialize monitoring
monitor = ModelMonitor(
    model_name="revenue_predictor",
    version="1.0.0"
)

# Define metrics
prediction_counter = prometheus_client.Counter(
    'model_predictions_total',
    'Total number of predictions'
)

prediction_latency = prometheus_client.Histogram(
    'model_prediction_duration_seconds',
    'Prediction latency'
)

drift_gauge = prometheus_client.Gauge(
    'model_drift_score',
    'Model drift detection score'
)

# Monitor predictions
@monitor.track_prediction
def monitored_predict(data):
    with prediction_latency.time():
        prediction = pipeline.predict(data)
        prediction_counter.inc()
        
        # Check for drift
        drift_score = monitor.calculate_drift(data)
        drift_gauge.set(drift_score)
        
        if drift_score > 0.7:
            monitor.alert("High drift detected!")
            
        return prediction

# A/B Testing Support
@monitor.ab_test("model_v2", split=0.1)
def predict_with_ab_test(data):
    # 10% traffic to new model
    if monitor.use_variant:
        return new_pipeline.predict(data)
    return pipeline.predict(data)

# Performance Dashboard
monitor.start_dashboard(port=3000)

# Automated Retraining Trigger
@monitor.on_performance_degradation(threshold=0.85)
def trigger_retraining():
    client.start_automl(
        dataset_id="latest_data",
        base_model=pipeline,
        improvement_threshold=0.02
    )`}
                  />
                </div>
              </div>
            )}

            {/* Integration Architecture */}
            <div style={{
              marginTop: '40px',
              background: 'rgba(255, 255, 255, 0.05)',
              padding: '40px',
              borderRadius: '16px'
            }}>
              <h2 style={{ margin: '0 0 30px 0', fontSize: '28px' }}>
                🏗️ Complete EvoML Integration Architecture
              </h2>
              
              <div style={{
                background: 'rgba(0, 0, 0, 0.3)',
                padding: '30px',
                borderRadius: '12px',
                textAlign: 'center'
              }}>
                <svg viewBox="0 0 800 500" style={{ maxWidth: '100%', height: 'auto' }}>
                  {/* Data Sources */}
                  <rect x="50" y="50" width="120" height="80" rx="8" fill="rgba(156, 39, 176, 0.2)" stroke="#9c27b0" strokeWidth="2"/>
                  <text x="110" y="85" textAnchor="middle" fill="white" fontSize="14" fontWeight="600">Data Sources</text>
                  <text x="110" y="105" textAnchor="middle" fill="white" fontSize="20">💾</text>
                  
                  {/* EvoML Platform */}
                  <rect x="300" y="150" width="200" height="200" rx="8" fill="rgba(233, 30, 99, 0.2)" stroke="#e91e63" strokeWidth="2"/>
                  <text x="400" y="180" textAnchor="middle" fill="white" fontSize="16" fontWeight="600">EvoML Platform</text>
                  <text x="400" y="210" textAnchor="middle" fill="white" fontSize="32">🧬</text>
                  <text x="400" y="240" textAnchor="middle" fill="white" fontSize="12">AutoML Engine</text>
                  <text x="400" y="260" textAnchor="middle" fill="white" fontSize="12">Code Generation</text>
                  <text x="400" y="280" textAnchor="middle" fill="white" fontSize="12">Multi-Objective Opt</text>
                  
                  {/* Generated Code */}
                  <rect x="600" y="50" width="140" height="80" rx="8" fill="rgba(33, 150, 243, 0.2)" stroke="#2196f3" strokeWidth="2"/>
                  <text x="670" y="85" textAnchor="middle" fill="white" fontSize="14" fontWeight="600">Python Code</text>
                  <text x="670" y="105" textAnchor="middle" fill="white" fontSize="20">📦</text>
                  
                  {/* Local Deployment */}
                  <rect x="600" y="200" width="140" height="80" rx="8" fill="rgba(76, 175, 80, 0.2)" stroke="#4caf50" strokeWidth="2"/>
                  <text x="670" y="235" textAnchor="middle" fill="white" fontSize="14" fontWeight="600">Local Deploy</text>
                  <text x="670" y="255" textAnchor="middle" fill="white" fontSize="20">🏠</text>
                  
                  {/* Cloud Deployment */}
                  <rect x="600" y="320" width="140" height="80" rx="8" fill="rgba(33, 150, 243, 0.2)" stroke="#2196f3" strokeWidth="2"/>
                  <text x="670" y="355" textAnchor="middle" fill="white" fontSize="14" fontWeight="600">Cloud Deploy</text>
                  <text x="670" y="375" textAnchor="middle" fill="white" fontSize="20">☁️</text>
                  
                  {/* Applications */}
                  <rect x="50" y="320" width="120" height="80" rx="8" fill="rgba(255, 152, 0, 0.2)" stroke="#ff9800" strokeWidth="2"/>
                  <text x="110" y="355" textAnchor="middle" fill="white" fontSize="14" fontWeight="600">Applications</text>
                  <text x="110" y="375" textAnchor="middle" fill="white" fontSize="20">📱</text>
                  
                  {/* Arrows */}
                  <path d="M 170 90 L 300 200" stroke="#9c27b0" strokeWidth="2" fill="none" markerEnd="url(#arrowhead)"/>
                  <path d="M 500 200 L 600 90" stroke="#2196f3" strokeWidth="2" fill="none" markerEnd="url(#arrowhead)"/>
                  <path d="M 500 250 L 600 240" stroke="#4caf50" strokeWidth="2" fill="none" markerEnd="url(#arrowhead)"/>
                  <path d="M 500 300 L 600 360" stroke="#2196f3" strokeWidth="2" fill="none" markerEnd="url(#arrowhead)"/>
                  <path d="M 170 360 L 300 300" stroke="#ff9800" strokeWidth="2" fill="none" markerEnd="url(#arrowhead)"/>
                  <path d="M 600 240 L 400 300" stroke="#4caf50" strokeWidth="2" fill="none" strokeDasharray="5,5"/>
                  
                  {/* Arrow marker */}
                  <defs>
                    <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                      <polygon points="0 0, 10 3.5, 0 7" fill="white" />
                    </marker>
                  </defs>
                </svg>
              </div>
            </div>

            {/* Benefits Summary */}
            <div style={{
              marginTop: '40px',
              background: 'linear-gradient(135deg, rgba(156, 39, 176, 0.1), rgba(233, 30, 99, 0.1))',
              padding: '40px',
              borderRadius: '16px',
              textAlign: 'center'
            }}>
              <h2 style={{ margin: '0 0 30px 0', fontSize: '28px' }}>
                🎯 Why EvoML for AutoML?
              </h2>
              
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                gap: '30px',
                maxWidth: '1000px',
                margin: '0 auto'
              }}>
                {[
                  { icon: '💻', title: 'Own Your Code', desc: 'No vendor lock-in, modify as needed' },
                  { icon: '🚀', title: '10x Faster Development', desc: 'Hours instead of months' },
                  { icon: '💰', title: '90% Cost Reduction', desc: 'One-time generation, unlimited use' },
                  { icon: '🏠', title: 'Deploy Anywhere', desc: 'Local, cloud, edge - your choice' }
                ].map((benefit, i) => (
                  <div key={i} style={{
                    background: 'rgba(255, 255, 255, 0.05)',
                    padding: '30px',
                    borderRadius: '12px',
                    border: '1px solid rgba(255, 255, 255, 0.1)'
                  }}>
                    <div style={{ fontSize: '48px', marginBottom: '15px' }}>{benefit.icon}</div>
                    <h3 style={{ margin: '0 0 10px 0', fontSize: '18px' }}>{benefit.title}</h3>
                    <p style={{ margin: 0, fontSize: '14px', opacity: 0.8 }}>{benefit.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* User Stories View */}
        {selectedView === 'stories' && (
          <div>
            {/* User Personas Header */}
            <div style={{
              background: 'linear-gradient(135deg, rgba(233, 30, 99, 0.1), rgba(156, 39, 176, 0.1))',
              padding: '40px',
              borderRadius: '16px',
              marginBottom: '40px',
              textAlign: 'center'
            }}>
              <h2 style={{ 
                margin: '0 0 20px 0', 
                fontSize: '36px',
                background: 'linear-gradient(135deg, #e91e63, #9c27b0)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                🎭 Real-World EvoML Stories
              </h2>
              <p style={{ 
                fontSize: '18px', 
                opacity: 0.9,
                maxWidth: '800px',
                margin: '0 auto'
              }}>
                See how different users leverage EvoML's AutoML capabilities to transform their ML workflows
              </p>
            </div>

            {/* Data Scientist Stories */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              padding: '40px',
              borderRadius: '16px',
              marginBottom: '30px'
            }}>
              <h3 style={{ 
                margin: '0 0 30px 0', 
                fontSize: '28px',
                color: '#e91e63',
                display: 'flex',
                alignItems: 'center',
                gap: '15px'
              }}>
                <span style={{ fontSize: '36px' }}>👨‍🔬</span>
                Data Scientist Stories
              </h3>

              {/* Story 1: Rapid Prototyping */}
              <div style={{
                background: 'rgba(233, 30, 99, 0.1)',
                padding: '30px',
                borderRadius: '12px',
                marginBottom: '25px',
                border: '1px solid rgba(233, 30, 99, 0.3)'
              }}>
                <h4 style={{ margin: '0 0 15px 0', color: '#e91e63', fontSize: '20px' }}>
                  "I need a model for tomorrow's presentation"
                </h4>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 2fr',
                  gap: '30px',
                  alignItems: 'center'
                }}>
                  <div style={{
                    background: 'rgba(0, 0, 0, 0.3)',
                    padding: '20px',
                    borderRadius: '8px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '48px', marginBottom: '10px' }}>⚡</div>
                    <div style={{ fontSize: '16px', fontWeight: '600' }}>Speed to Value</div>
                    <div style={{ fontSize: '14px', opacity: 0.8, marginTop: '5px' }}>
                      Hours not Weeks
                    </div>
                  </div>
                  <div>
                    <p style={{ fontSize: '16px', lineHeight: '1.6', marginBottom: '15px' }}>
                      <strong>Scenario:</strong> CEO wants predictive model for customer churn by tomorrow. 
                      I have the data but no time to build pipelines.
                    </p>
                    <div style={{
                      background: 'rgba(0, 0, 0, 0.2)',
                      padding: '15px',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}>
                      <strong>What happens with EvoML:</strong>
                      <ul style={{ margin: '10px 0 0 20px' }}>
                        <li>Upload customer data CSV (5 minutes)</li>
                        <li>EvoML identifies churn patterns automatically</li>
                        <li>Run AutoML with business constraints (30 minutes)</li>
                        <li>Download production-ready code</li>
                        <li>Deploy locally and create dashboard</li>
                        <li>Present working model next morning</li>
                      </ul>
                    </div>
                    <div style={{
                      marginTop: '15px',
                      padding: '10px 15px',
                      background: 'rgba(233, 30, 99, 0.2)',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}>
                      💡 Result: 92% accurate model delivered in 3 hours vs 2 weeks traditional approach
                    </div>
                  </div>
                </div>
              </div>

              {/* Story 2: Complex Feature Engineering */}
              <div style={{
                background: 'rgba(156, 39, 176, 0.1)',
                padding: '30px',
                borderRadius: '12px',
                marginBottom: '25px',
                border: '1px solid rgba(156, 39, 176, 0.3)'
              }}>
                <h4 style={{ margin: '0 0 15px 0', color: '#9c27b0', fontSize: '20px' }}>
                  "I'm stuck on feature engineering for time series"
                </h4>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 2fr',
                  gap: '30px',
                  alignItems: 'center'
                }}>
                  <div style={{
                    background: 'rgba(0, 0, 0, 0.3)',
                    padding: '20px',
                    borderRadius: '8px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '48px', marginBottom: '10px' }}>🧬</div>
                    <div style={{ fontSize: '16px', fontWeight: '600' }}>Automated Features</div>
                    <div style={{ fontSize: '14px', opacity: 0.8, marginTop: '5px' }}>
                      1000s of Combinations
                    </div>
                  </div>
                  <div>
                    <p style={{ fontSize: '16px', lineHeight: '1.6', marginBottom: '15px' }}>
                      <strong>Scenario:</strong> Sales forecasting with complex seasonality, promotions, 
                      and external factors. Manual feature engineering taking weeks.
                    </p>
                    <div style={{
                      background: 'rgba(0, 0, 0, 0.2)',
                      padding: '15px',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}>
                      <strong>EvoML's automated approach:</strong>
                      <ul style={{ margin: '10px 0 0 20px' }}>
                        <li>Detects multiple seasonality patterns automatically</li>
                        <li>Creates lag features with optimal windows</li>
                        <li>Generates interaction features (promotion × season)</li>
                        <li>Adds external data correlations</li>
                        <li>Tests 1000s of feature combinations</li>
                        <li>Outputs best feature engineering code</li>
                      </ul>
                    </div>
                    <div style={{
                      marginTop: '15px',
                      padding: '10px 15px',
                      background: 'rgba(156, 39, 176, 0.2)',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}>
                      💡 Result: Discovered 47 powerful features I would never have thought of
                    </div>
                  </div>
                </div>
              </div>

              {/* Story 3: Multi-Objective Optimization */}
              <div style={{
                background: 'rgba(33, 150, 243, 0.1)',
                padding: '30px',
                borderRadius: '12px',
                border: '1px solid rgba(33, 150, 243, 0.3)'
              }}>
                <h4 style={{ margin: '0 0 15px 0', color: '#2196f3', fontSize: '20px' }}>
                  "I need accuracy AND speed for edge deployment"
                </h4>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 2fr',
                  gap: '30px',
                  alignItems: 'center'
                }}>
                  <div style={{
                    background: 'rgba(0, 0, 0, 0.3)',
                    padding: '20px',
                    borderRadius: '8px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '48px', marginBottom: '10px' }}>🎯</div>
                    <div style={{ fontSize: '16px', fontWeight: '600' }}>Multi-Objective</div>
                    <div style={{ fontSize: '14px', opacity: 0.8, marginTop: '5px' }}>
                      Balance Trade-offs
                    </div>
                  </div>
                  <div>
                    <p style={{ fontSize: '16px', lineHeight: '1.6', marginBottom: '15px' }}>
                      <strong>Scenario:</strong> IoT anomaly detection model needs 95%+ accuracy but must 
                      run on Raspberry Pi with &lt;10ms latency.
                    </p>
                    <div style={{
                      background: 'rgba(0, 0, 0, 0.2)',
                      padding: '15px',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}>
                      <strong>EvoML multi-objective optimization:</strong>
                      <ul style={{ margin: '10px 0 0 20px' }}>
                        <li>Set constraints: max 5MB model, &lt;10ms inference</li>
                        <li>Optimize for: accuracy (0.6 weight) + speed (0.4 weight)</li>
                        <li>EvoML explores model architectures:</li>
                        <ul style={{ marginLeft: '20px', marginTop: '5px' }}>
                          <li>Quantized neural networks</li>
                          <li>Optimized decision trees</li>
                          <li>Lightweight ensemble methods</li>
                        </ul>
                        <li>Generates Pareto front of solutions</li>
                        <li>Choose best trade-off for needs</li>
                      </ul>
                    </div>
                    <div style={{
                      marginTop: '15px',
                      padding: '10px 15px',
                      background: 'rgba(33, 150, 243, 0.2)',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}>
                      💡 Result: 96.3% accuracy with 7ms inference on Pi - impossible manually
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ML Engineer Stories */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              padding: '40px',
              borderRadius: '16px',
              marginBottom: '30px'
            }}>
              <h3 style={{ 
                margin: '0 0 30px 0', 
                fontSize: '28px',
                color: '#9c27b0',
                display: 'flex',
                alignItems: 'center',
                gap: '15px'
              }}>
                <span style={{ fontSize: '36px' }}>👨‍💻</span>
                ML Engineer Stories
              </h3>

              {/* Story 4: Production Pipeline */}
              <div style={{
                background: 'rgba(76, 175, 80, 0.1)',
                padding: '30px',
                borderRadius: '12px',
                marginBottom: '25px',
                border: '1px solid rgba(76, 175, 80, 0.3)'
              }}>
                <h4 style={{ margin: '0 0 15px 0', color: '#4caf50', fontSize: '20px' }}>
                  "I need production-grade code, not notebooks"
                </h4>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 2fr',
                  gap: '30px',
                  alignItems: 'center'
                }}>
                  <div style={{
                    background: 'rgba(0, 0, 0, 0.3)',
                    padding: '20px',
                    borderRadius: '8px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '48px', marginBottom: '10px' }}>🏭</div>
                    <div style={{ fontSize: '16px', fontWeight: '600' }}>Production Ready</div>
                    <div style={{ fontSize: '14px', opacity: 0.8, marginTop: '5px' }}>
                      Clean, Tested Code
                    </div>
                  </div>
                  <div>
                    <p style={{ fontSize: '16px', lineHeight: '1.6', marginBottom: '15px' }}>
                      <strong>Scenario:</strong> Data scientists gave me Jupyter notebooks. I need to 
                      convert to production API with proper error handling, logging, and monitoring.
                    </p>
                    <div style={{
                      background: 'rgba(0, 0, 0, 0.2)',
                      padding: '15px',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}>
                      <strong>EvoML generates complete package:</strong>
                      <ul style={{ margin: '10px 0 0 20px' }}>
                        <li>Modular Python package structure</li>
                        <li>FastAPI server with validation</li>
                        <li>Comprehensive error handling</li>
                        <li>Prometheus metrics integration</li>
                        <li>Docker & Kubernetes configs</li>
                        <li>Unit and integration tests</li>
                        <li>CI/CD pipeline templates</li>
                      </ul>
                    </div>
                    <div style={{
                      marginTop: '15px',
                      padding: '10px 15px',
                      background: 'rgba(76, 175, 80, 0.2)',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}>
                      💡 Result: 2 weeks of engineering work automated, deployed in 1 hour
                    </div>
                  </div>
                </div>
              </div>

              {/* Story 5: Model Versioning */}
              <div style={{
                background: 'rgba(255, 152, 0, 0.1)',
                padding: '30px',
                borderRadius: '12px',
                border: '1px solid rgba(255, 152, 0, 0.3)'
              }}>
                <h4 style={{ margin: '0 0 15px 0', color: '#ff9800', fontSize: '20px' }}>
                  "We need A/B testing for model updates"
                </h4>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 2fr',
                  gap: '30px',
                  alignItems: 'center'
                }}>
                  <div style={{
                    background: 'rgba(0, 0, 0, 0.3)',
                    padding: '20px',
                    borderRadius: '8px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '48px', marginBottom: '10px' }}>🔀</div>
                    <div style={{ fontSize: '16px', fontWeight: '600' }}>Smart Routing</div>
                    <div style={{ fontSize: '14px', opacity: 0.8, marginTop: '5px' }}>
                      Built-in A/B
                    </div>
                  </div>
                  <div>
                    <p style={{ fontSize: '16px', lineHeight: '1.6', marginBottom: '15px' }}>
                      <strong>Scenario:</strong> New model version ready but need gradual rollout with 
                      monitoring and automatic rollback if performance drops.
                    </p>
                    <div style={{
                      background: 'rgba(0, 0, 0, 0.2)',
                      padding: '15px',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}>
                      <strong>Generated code includes:</strong>
                      <ul style={{ margin: '10px 0 0 20px' }}>
                        <li>Traffic splitting logic (canary deployment)</li>
                        <li>Performance comparison metrics</li>
                        <li>Automatic rollback triggers</li>
                        <li>User segment targeting</li>
                        <li>Statistical significance testing</li>
                        <li>Gradual rollout automation</li>
                      </ul>
                    </div>
                    <div style={{
                      marginTop: '15px',
                      padding: '10px 15px',
                      background: 'rgba(255, 152, 0, 0.2)',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}>
                      💡 Result: Safe model updates with 0 downtime and automatic optimization
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Business Analyst Stories */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              padding: '40px',
              borderRadius: '16px',
              marginBottom: '30px'
            }}>
              <h3 style={{ 
                margin: '0 0 30px 0', 
                fontSize: '28px',
                color: '#2196f3',
                display: 'flex',
                alignItems: 'center',
                gap: '15px'
              }}>
                <span style={{ fontSize: '36px' }}>📊</span>
                Business Analyst Stories
              </h3>

              {/* Story 6: No-Code ML */}
              <div style={{
                background: 'rgba(33, 150, 243, 0.1)',
                padding: '30px',
                borderRadius: '12px',
                marginBottom: '25px',
                border: '1px solid rgba(33, 150, 243, 0.3)'
              }}>
                <h4 style={{ margin: '0 0 15px 0', color: '#2196f3', fontSize: '20px' }}>
                  "I need predictions but don't code"
                </h4>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 2fr',
                  gap: '30px',
                  alignItems: 'center'
                }}>
                  <div style={{
                    background: 'rgba(0, 0, 0, 0.3)',
                    padding: '20px',
                    borderRadius: '8px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '48px', marginBottom: '10px' }}>🎨</div>
                    <div style={{ fontSize: '16px', fontWeight: '600' }}>Visual Interface</div>
                    <div style={{ fontSize: '14px', opacity: 0.8, marginTop: '5px' }}>
                      No Code Required
                    </div>
                  </div>
                  <div>
                    <p style={{ fontSize: '16px', lineHeight: '1.6', marginBottom: '15px' }}>
                      <strong>Scenario:</strong> Need to predict customer lifetime value for marketing 
                      campaigns. Have Excel data but no programming skills.
                    </p>
                    <div style={{
                      background: 'rgba(0, 0, 0, 0.2)',
                      padding: '15px',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}>
                      <strong>Using EvoML web interface:</strong>
                      <ul style={{ margin: '10px 0 0 20px' }}>
                        <li>Upload Excel file via drag-and-drop</li>
                        <li>Select "Customer Lifetime Value" template</li>
                        <li>Point to target column visually</li>
                        <li>Click "Generate Model"</li>
                        <li>Get predictions in new Excel file</li>
                        <li>Share web app link with team</li>
                      </ul>
                    </div>
                    <div style={{
                      marginTop: '15px',
                      padding: '10px 15px',
                      background: 'rgba(33, 150, 243, 0.2)',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}>
                      💡 Result: ML predictions without writing a single line of code
                    </div>
                  </div>
                </div>
              </div>

              {/* Story 7: Custom KPIs */}
              <div style={{
                background: 'rgba(156, 39, 176, 0.1)',
                padding: '30px',
                borderRadius: '12px',
                border: '1px solid rgba(156, 39, 176, 0.3)'
              }}>
                <h4 style={{ margin: '0 0 15px 0', color: '#9c27b0', fontSize: '20px' }}>
                  "Standard metrics don't match our business"
                </h4>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 2fr',
                  gap: '30px',
                  alignItems: 'center'
                }}>
                  <div style={{
                    background: 'rgba(0, 0, 0, 0.3)',
                    padding: '20px',
                    borderRadius: '8px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '48px', marginBottom: '10px' }}>📈</div>
                    <div style={{ fontSize: '16px', fontWeight: '600' }}>Business Metrics</div>
                    <div style={{ fontSize: '14px', opacity: 0.8, marginTop: '5px' }}>
                      Custom Optimization
                    </div>
                  </div>
                  <div>
                    <p style={{ fontSize: '16px', lineHeight: '1.6', marginBottom: '15px' }}>
                      <strong>Scenario:</strong> Need to optimize for customer satisfaction score weighted 
                      by customer tier and revenue impact.
                    </p>
                    <div style={{
                      background: 'rgba(0, 0, 0, 0.2)',
                      padding: '15px',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}>
                      <strong>EvoML custom metric support:</strong>
                      <ul style={{ margin: '10px 0 0 20px' }}>
                        <li>Define custom business metric in UI</li>
                        <li>Weight by customer segments</li>
                        <li>Include revenue impact calculations</li>
                        <li>EvoML optimizes for YOUR metric</li>
                        <li>Generated code includes metric logic</li>
                        <li>Dashboard shows business impact</li>
                      </ul>
                    </div>
                    <div style={{
                      marginTop: '15px',
                      padding: '10px 15px',
                      background: 'rgba(156, 39, 176, 0.2)',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}>
                      💡 Result: Model that actually optimizes for business value, not just accuracy
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Enterprise IT Stories */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              padding: '40px',
              borderRadius: '16px',
              marginBottom: '30px'
            }}>
              <h3 style={{ 
                margin: '0 0 30px 0', 
                fontSize: '28px',
                color: '#ff9800',
                display: 'flex',
                alignItems: 'center',
                gap: '15px'
              }}>
                <span style={{ fontSize: '36px' }}>🏢</span>
                Enterprise IT Stories
              </h3>

              {/* Story 8: On-Premise Requirement */}
              <div style={{
                background: 'rgba(255, 152, 0, 0.1)',
                padding: '30px',
                borderRadius: '12px',
                marginBottom: '25px',
                border: '1px solid rgba(255, 152, 0, 0.3)'
              }}>
                <h4 style={{ margin: '0 0 15px 0', color: '#ff9800', fontSize: '20px' }}>
                  "Our data cannot leave the premises"
                </h4>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 2fr',
                  gap: '30px',
                  alignItems: 'center'
                }}>
                  <div style={{
                    background: 'rgba(0, 0, 0, 0.3)',
                    padding: '20px',
                    borderRadius: '8px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '48px', marginBottom: '10px' }}>🔒</div>
                    <div style={{ fontSize: '16px', fontWeight: '600' }}>Full Privacy</div>
                    <div style={{ fontSize: '14px', opacity: 0.8, marginTop: '5px' }}>
                      100% On-Premise
                    </div>
                  </div>
                  <div>
                    <p style={{ fontSize: '16px', lineHeight: '1.6', marginBottom: '15px' }}>
                      <strong>Scenario:</strong> Healthcare data with strict HIPAA requirements. Need ML 
                      but absolutely no cloud processing allowed.
                    </p>
                    <div style={{
                      background: 'rgba(0, 0, 0, 0.2)',
                      padding: '15px',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}>
                      <strong>EvoML on-premise deployment:</strong>
                      <ul style={{ margin: '10px 0 0 20px' }}>
                        <li>Install EvoML on local servers</li>
                        <li>All processing happens locally</li>
                        <li>Generated code runs entirely offline</li>
                        <li>No external API calls in production</li>
                        <li>Air-gapped environment support</li>
                        <li>Full audit trail for compliance</li>
                      </ul>
                    </div>
                    <div style={{
                      marginTop: '15px',
                      padding: '10px 15px',
                      background: 'rgba(255, 152, 0, 0.2)',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}>
                      💡 Result: Enterprise ML with zero data exposure, full compliance
                    </div>
                  </div>
                </div>
              </div>

              {/* Story 9: Cost Control */}
              <div style={{
                background: 'rgba(76, 175, 80, 0.1)',
                padding: '30px',
                borderRadius: '12px',
                border: '1px solid rgba(76, 175, 80, 0.3)'
              }}>
                <h4 style={{ margin: '0 0 15px 0', color: '#4caf50', fontSize: '20px' }}>
                  "Cloud ML APIs are costing us $50K/month"
                </h4>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 2fr',
                  gap: '30px',
                  alignItems: 'center'
                }}>
                  <div style={{
                    background: 'rgba(0, 0, 0, 0.3)',
                    padding: '20px',
                    borderRadius: '8px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '48px', marginBottom: '10px' }}>💰</div>
                    <div style={{ fontSize: '16px', fontWeight: '600' }}>90% Savings</div>
                    <div style={{ fontSize: '14px', opacity: 0.8, marginTop: '5px' }}>
                      One-Time Cost
                    </div>
                  </div>
                  <div>
                    <p style={{ fontSize: '16px', lineHeight: '1.6', marginBottom: '15px' }}>
                      <strong>Scenario:</strong> Using cloud provider's ML APIs for fraud detection. 
                      Processing 10M transactions daily = massive bills.
                    </p>
                    <div style={{
                      background: 'rgba(0, 0, 0, 0.2)',
                      padding: '15px',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}>
                      <strong>EvoML cost savings:</strong>
                      <ul style={{ margin: '10px 0 0 20px' }}>
                        <li>One-time model generation with EvoML</li>
                        <li>Deploy on existing infrastructure</li>
                        <li>Unlimited predictions at zero marginal cost</li>
                        <li>Performance equal or better than cloud APIs</li>
                        <li>Scale horizontally as needed</li>
                        <li>ROI in less than 2 months</li>
                      </ul>
                    </div>
                    <div style={{
                      marginTop: '15px',
                      padding: '10px 15px',
                      background: 'rgba(76, 175, 80, 0.2)',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}>
                      💡 Result: $600K annual savings, better performance, full control
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Value Summary Diagram */}
            <div style={{
              background: 'linear-gradient(135deg, rgba(156, 39, 176, 0.1), rgba(233, 30, 99, 0.1))',
              padding: '40px',
              borderRadius: '16px',
              marginTop: '40px'
            }}>
              <h3 style={{ 
                margin: '0 0 30px 0', 
                fontSize: '28px',
                textAlign: 'center'
              }}>
                🎯 EvoML Value Across All Users
              </h3>
              
              <div style={{
                background: 'rgba(0, 0, 0, 0.3)',
                padding: '30px',
                borderRadius: '12px'
              }}>
                <svg viewBox="0 0 900 500" style={{ maxWidth: '100%', height: 'auto' }}>
                  {/* Central EvoML Hub */}
                  <circle cx="450" cy="250" r="80" fill="rgba(233, 30, 99, 0.3)" stroke="#e91e63" strokeWidth="3"/>
                  <text x="450" y="240" textAnchor="middle" fill="white" fontSize="18" fontWeight="600">EvoML</text>
                  <text x="450" y="260" textAnchor="middle" fill="white" fontSize="24">🧬</text>
                  <text x="450" y="280" textAnchor="middle" fill="white" fontSize="12">AutoML</text>
                  
                  {/* User Personas */}
                  {[
                    { x: 150, y: 100, label: 'Data Scientist', icon: '👨‍🔬', color: '#e91e63' },
                    { x: 750, y: 100, label: 'ML Engineer', icon: '👨‍💻', color: '#9c27b0' },
                    { x: 150, y: 400, label: 'Business', icon: '📊', color: '#2196f3' },
                    { x: 750, y: 400, label: 'Enterprise IT', icon: '🏢', color: '#ff9800' },
                    { x: 450, y: 50, label: 'Researchers', icon: '🔬', color: '#4caf50' }
                  ].map((persona, i) => (
                    <g key={i}>
                      <circle cx={persona.x} cy={persona.y} r="60" fill={`${persona.color}20`} stroke={persona.color} strokeWidth="2"/>
                      <text x={persona.x} y={persona.y - 10} textAnchor="middle" fill="white" fontSize="24">{persona.icon}</text>
                      <text x={persona.x} y={persona.y + 20} textAnchor="middle" fill="white" fontSize="14">{persona.label}</text>
                      <line x1={persona.x} y1={persona.y} x2="450" y2="250" stroke={persona.color} strokeWidth="2" opacity="0.5"/>
                    </g>
                  ))}
                  
                  {/* Value Props */}
                  <text x="450" y="450" textAnchor="middle" fill="#e91e63" fontSize="16" fontWeight="600">Generated Code</text>
                  <text x="100" y="250" textAnchor="middle" fill="#9c27b0" fontSize="16" fontWeight="600">Speed</text>
                  <text x="800" y="250" textAnchor="middle" fill="#2196f3" fontSize="16" fontWeight="600">Control</text>
                  <text x="250" y="50" textAnchor="middle" fill="#4caf50" fontSize="16" fontWeight="600">Innovation</text>
                  <text x="650" y="50" textAnchor="middle" fill="#ff9800" fontSize="16" fontWeight="600">Savings</text>
                </svg>
              </div>
              
              <div style={{
                marginTop: '30px',
                textAlign: 'center',
                fontSize: '18px',
                fontWeight: '600'
              }}>
                One Platform • Complete Ownership • Unlimited Deployment
              </div>
            </div>

            {/* Why EvoML Section */}
            <div style={{
              background: 'linear-gradient(135deg, rgba(156, 39, 176, 0.1), rgba(233, 30, 99, 0.1))',
              padding: '40px',
              borderRadius: '16px',
              marginTop: '40px',
              border: '1px solid rgba(156, 39, 176, 0.3)'
            }}>
              <h3 style={{ 
                margin: '0 0 30px 0', 
                fontSize: '28px',
                textAlign: 'center',
                background: 'linear-gradient(135deg, #9c27b0, #e91e63)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                🤔 Why EvoML vs Other AutoML?
              </h3>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: '25px',
                marginBottom: '30px'
              }}>
                {/* Traditional AutoML Limitations */}
                <div style={{
                  background: 'rgba(255, 107, 107, 0.1)',
                  padding: '25px',
                  borderRadius: '12px',
                  border: '1px solid rgba(255, 107, 107, 0.3)'
                }}>
                  <h4 style={{ margin: '0 0 15px 0', color: '#FF6B6B' }}>
                    ❌ Other AutoML Platforms
                  </h4>
                  <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px', lineHeight: '1.8' }}>
                    <li><strong>Black box:</strong> Can't see or modify the model</li>
                    <li><strong>Vendor lock-in:</strong> Must use their API forever</li>
                    <li><strong>Recurring costs:</strong> Pay per prediction</li>
                    <li><strong>Limited deployment:</strong> Cloud-only options</li>
                    <li><strong>Generic models:</strong> Not optimized for your constraints</li>
                  </ul>
                </div>

                {/* EvoML Advantages */}
                <div style={{
                  background: 'rgba(156, 39, 176, 0.1)',
                  padding: '25px',
                  borderRadius: '12px',
                  border: '1px solid rgba(156, 39, 176, 0.3)'
                }}>
                  <h4 style={{ margin: '0 0 15px 0', color: '#9c27b0' }}>
                    ✅ EvoML Code Generation
                  </h4>
                  <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px', lineHeight: '1.8' }}>
                    <li><strong>Full transparency:</strong> Complete Python source code</li>
                    <li><strong>True ownership:</strong> Modify and extend as needed</li>
                    <li><strong>One-time cost:</strong> Generate once, use forever</li>
                    <li><strong>Deploy anywhere:</strong> Local, cloud, edge, air-gapped</li>
                    <li><strong>Custom optimization:</strong> Your metrics, your constraints</li>
                  </ul>
                </div>

                {/* Real Results */}
                <div style={{
                  background: 'rgba(76, 175, 80, 0.1)',
                  padding: '25px',
                  borderRadius: '12px',
                  border: '1px solid rgba(76, 175, 80, 0.3)'
                }}>
                  <h4 style={{ margin: '0 0 15px 0', color: '#4caf50' }}>
                    🚀 Real Customer Results
                  </h4>
                  <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px', lineHeight: '1.8' }}>
                    <li><strong>FinTech:</strong> $2M saved vs cloud ML APIs</li>
                    <li><strong>Healthcare:</strong> HIPAA compliant on-premise ML</li>
                    <li><strong>Retail:</strong> 10x faster model development</li>
                    <li><strong>IoT:</strong> Edge models with 5ms latency</li>
                    <li><strong>Enterprise:</strong> 100% data privacy maintained</li>
                  </ul>
                </div>
              </div>

              {/* Key Differentiator */}
              <div style={{
                background: 'rgba(0, 0, 0, 0.3)',
                padding: '30px',
                borderRadius: '12px',
                textAlign: 'center'
              }}>
                <h4 style={{ 
                  margin: '0 0 20px 0', 
                  fontSize: '24px',
                  color: '#e91e63'
                }}>
                  The EvoML Difference
                </h4>
                <p style={{ 
                  fontSize: '18px', 
                  lineHeight: '1.6',
                  maxWidth: '800px',
                  margin: '0 auto'
                }}>
                  <strong>Other AutoML platforms give you a model. EvoML gives you the code.</strong>
                </p>
                <p style={{ 
                  fontSize: '16px', 
                  lineHeight: '1.6',
                  opacity: 0.9,
                  marginTop: '15px',
                  maxWidth: '800px',
                  margin: '15px auto 0'
                }}>
                  While competitors lock you into their ecosystem with API-only access, EvoML generates 
                  complete, production-ready Python code that you own, modify, and deploy wherever you want. 
                  No vendor lock-in. No recurring costs. Just great ML code, optimized for your needs.
                </p>
              </div>

              {/* CTA Message */}
              <div style={{
                marginTop: '30px',
                padding: '20px',
                background: 'linear-gradient(135deg, rgba(156, 39, 176, 0.2), rgba(233, 30, 99, 0.2))',
                borderRadius: '12px',
                textAlign: 'center'
              }}>
                <p style={{ 
                  margin: 0,
                  fontSize: '16px',
                  fontWeight: '600'
                }}>
                  🧬 EvoML: The AutoML platform that sets your models free
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EvoMLSDLC; 